import React, { useState, useEffect, useCallback } from 'react';
import { contactsAPI, contactGroupsAPI } from '../services/api';
import { useOffline } from '../context/OfflineContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Search,
  Plus,
  User,
  Mail,
  Phone,
  Building,
  MapPin,
  MoreVertical,
  Edit,
  Trash2,
  UserPlus,
  Users,
  Tag,
  Download,
  Upload,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    job_title: '',
    address: '',
    notes: '',
    group_ids: [],
  });
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupColor, setNewGroupColor] = useState('#0066DA');
  const { isOnline, cacheContacts, getCachedContacts } = useOffline();

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await contactsAPI.getContacts();
      setContacts(response.data.contacts);
      cacheContacts(response.data.contacts);
    } catch (error) {
      console.error('Failed to load contacts:', error);
      if (!isOnline) {
        const cached = await getCachedContacts();
        if (cached.length > 0) {
          setContacts(cached);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [isOnline, cacheContacts, getCachedContacts]);

  const loadGroups = useCallback(async () => {
    try {
      const response = await contactGroupsAPI.getGroups();
      setGroups(response.data.groups);
    } catch (error) {
      console.error('Failed to load groups:', error);
    }
  }, []);

  useEffect(() => {
    loadContacts();
    loadGroups();
  }, [loadContacts, loadGroups]);

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = searchQuery === '' || 
      `${contact.first_name} ${contact.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesGroup = selectedGroup === null || 
      contact.group_ids?.includes(selectedGroup.id);
    
    return matchesSearch && matchesGroup;
  });

  const openNewContact = () => {
    setContactForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company: '',
      job_title: '',
      address: '',
      notes: '',
      group_ids: [],
    });
    setEditingContact(null);
    setDialogOpen(true);
  };

  const openEditContact = (contact) => {
    setContactForm({
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email,
      phone: contact.phone || '',
      company: contact.company || '',
      job_title: contact.job_title || '',
      address: contact.address || '',
      notes: contact.notes || '',
      group_ids: contact.group_ids || [],
    });
    setEditingContact(contact);
    setDialogOpen(true);
  };

  const handleSaveContact = async () => {
    if (!contactForm.first_name.trim() || !contactForm.email.trim()) {
      toast.error('Prénom et email requis');
      return;
    }

    try {
      if (editingContact) {
        await contactsAPI.updateContact(editingContact.id, contactForm);
        toast.success('Contact modifié');
      } else {
        await contactsAPI.createContact(contactForm);
        toast.success('Contact créé');
      }

      setDialogOpen(false);
      loadContacts();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await contactsAPI.deleteContact(contactId);
      if (selectedContact?.id === contactId) {
        setSelectedContact(null);
      }
      toast.success('Contact supprimé');
      loadContacts();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;

    try {
      await contactGroupsAPI.createGroup({ name: newGroupName, color: newGroupColor });
      toast.success('Groupe créé');
      setNewGroupName('');
      setGroupDialogOpen(false);
      loadGroups();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      await contactGroupsAPI.deleteGroup(groupId);
      if (selectedGroup?.id === groupId) {
        setSelectedGroup(null);
      }
      toast.success('Groupe supprimé');
      loadGroups();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getInitials = (contact) => {
    return `${contact.first_name?.[0] || ''}${contact.last_name?.[0] || ''}`.toUpperCase() || 'C';
  };

  const handleExportContacts = async (format = 'csv') => {
    try {
      const response = await contactsAPI.exportContacts(format);
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'vcard' ? 'contacts.vcf' : 'contacts.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success(`Contacts exportés en ${format.toUpperCase()}`);
    } catch (error) {
      toast.error("Erreur lors de l'exportation");
    }
  };

  const handleImportContacts = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const response = await contactsAPI.importContacts(file);
      toast.success(response.data.message);
      loadContacts();
    } catch (error) {
      toast.error("Erreur lors de l'importation");
    }
    e.target.value = '';
  };

  const getGroupById = (groupId) => groups.find(g => g.id === groupId);

  return (
    <div className="h-full flex">
      {/* Groups sidebar */}
      <div className="w-60 border-r border-border bg-card flex flex-col">
        <div className="p-4">
          <Button className="w-full gap-2" onClick={openNewContact} data-testid="new-contact-btn">
            <UserPlus className="w-4 h-4" />
            Nouveau contact
          </Button>
        </div>

        <ScrollArea className="flex-1">
          <div className="px-3 py-2">
            <div className="flex items-center justify-between mb-2 px-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Groupes
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="w-6 h-6"
                onClick={() => setGroupDialogOpen(true)}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            <Button
              variant="ghost"
              className={cn(
                "w-full justify-start gap-3 h-10 mb-1",
                selectedGroup === null && "bg-primary/10 text-primary"
              )}
              onClick={() => setSelectedGroup(null)}
            >
              <Users className="w-4 h-4" />
              <span className="flex-1 text-left">Tous les contacts</span>
              <Badge variant="secondary">{contacts.length}</Badge>
            </Button>

            {groups.map((group) => {
              const groupContacts = contacts.filter(c => c.group_ids?.includes(group.id));
              return (
                <div key={group.id} className="flex items-center gap-1 mb-1">
                  <Button
                    variant="ghost"
                    className={cn(
                      "flex-1 justify-start gap-3 h-10",
                      selectedGroup?.id === group.id && "bg-primary/10 text-primary"
                    )}
                    onClick={() => setSelectedGroup(group)}
                  >
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: group.color }}
                    />
                    <span className="flex-1 text-left truncate">{group.name}</span>
                    <Badge variant="secondary">{groupContacts.length}</Badge>
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="w-8 h-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => handleDeleteGroup(group.id)}
                        className="text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </div>

      {/* Contacts list */}
      <div className="w-80 border-r border-border flex flex-col bg-background">
        <div className="p-3 border-b border-border space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher..."
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="contact-search-input"
            />
          </div>
          <div className="flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5 flex-1" data-testid="export-contacts-btn">
                  <Download className="w-3.5 h-3.5" />
                  Exporter
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => handleExportContacts('csv')}>
                  Exporter en CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExportContacts('vcard')}>
                  Exporter en vCard
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" className="gap-1.5 flex-1" data-testid="import-contacts-btn" asChild>
              <label className="cursor-pointer">
                <Upload className="w-3.5 h-3.5" />
                Importer
                <input type="file" accept=".csv,.vcf" className="hidden" onChange={handleImportContacts} />
              </label>
            </Button>
          </div>
        </div>

        <ScrollArea className="flex-1">
          {filteredContacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <User className="w-12 h-12 mb-3 opacity-50" />
              <p>Aucun contact</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredContacts.map((contact) => (
                <div
                  key={contact.id}
                  className={cn(
                    "p-3 cursor-pointer hover:bg-muted/50 transition-colors",
                    selectedContact?.id === contact.id && "bg-primary/5"
                  )}
                  onClick={() => setSelectedContact(contact)}
                  data-testid={`contact-item-${contact.id}`}
                >
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {getInitials(contact)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {contact.first_name} {contact.last_name}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        {contact.email}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Contact detail */}
      <div className="flex-1 flex flex-col bg-background">
        {selectedContact ? (
          <>
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="text-lg font-semibold font-['Plus_Jakarta_Sans']">
                Détails du contact
              </h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => openEditContact(selectedContact)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive"
                  onClick={() => handleDeleteContact(selectedContact.id)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
              </div>
            </div>

            <ScrollArea className="flex-1">
              <div className="p-6">
                <div className="flex items-center gap-4 mb-8">
                  <Avatar className="w-20 h-20">
                    <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                      {getInitials(selectedContact)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="text-2xl font-bold font-['Plus_Jakarta_Sans']">
                      {selectedContact.first_name} {selectedContact.last_name}
                    </h3>
                    {selectedContact.job_title && (
                      <p className="text-muted-foreground">{selectedContact.job_title}</p>
                    )}
                    {selectedContact.company && (
                      <p className="text-sm text-muted-foreground">{selectedContact.company}</p>
                    )}
                  </div>
                </div>

                {selectedContact.group_ids?.length > 0 && (
                  <div className="mb-6">
                    <div className="flex flex-wrap gap-2">
                      {selectedContact.group_ids.map(groupId => {
                        const group = getGroupById(groupId);
                        return group ? (
                          <Badge
                            key={groupId}
                            variant="secondary"
                            className="gap-1"
                            style={{ backgroundColor: `${group.color}20`, color: group.color }}
                          >
                            <Tag className="w-3 h-3" />
                            {group.name}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <Mail className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <a href={`mailto:${selectedContact.email}`} className="text-primary hover:underline">
                        {selectedContact.email}
                      </a>
                    </div>
                  </div>

                  {selectedContact.phone && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <Phone className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Téléphone</p>
                        <a href={`tel:${selectedContact.phone}`} className="hover:underline">
                          {selectedContact.phone}
                        </a>
                      </div>
                    </div>
                  )}

                  {selectedContact.company && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <Building className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Entreprise</p>
                        <p>{selectedContact.company}</p>
                      </div>
                    </div>
                  )}

                  {selectedContact.address && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <MapPin className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Adresse</p>
                        <p>{selectedContact.address}</p>
                      </div>
                    </div>
                  )}

                  {selectedContact.notes && (
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-sm text-muted-foreground mb-2">Notes</p>
                      <p className="whitespace-pre-wrap">{selectedContact.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
            <User className="w-16 h-16 mb-4 opacity-30" />
            <p className="text-lg">Sélectionnez un contact</p>
            <p className="text-sm">pour voir ses détails</p>
          </div>
        )}
      </div>

      {/* Contact Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans']">
              {editingContact ? 'Modifier le contact' : 'Nouveau contact'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prénom *</Label>
                <Input
                  placeholder="Prénom"
                  value={contactForm.first_name}
                  onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })}
                  data-testid="contact-firstname-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input
                  placeholder="Nom"
                  value={contactForm.last_name}
                  onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder="email@exemple.com"
                  className="pl-10"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  data-testid="contact-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Téléphone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="+33 6 12 34 56 78"
                  className="pl-10"
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Entreprise</Label>
                <Input
                  placeholder="Entreprise"
                  value={contactForm.company}
                  onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Poste</Label>
                <Input
                  placeholder="Poste"
                  value={contactForm.job_title}
                  onChange={(e) => setContactForm({ ...contactForm, job_title: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input
                placeholder="Adresse complète"
                value={contactForm.address}
                onChange={(e) => setContactForm({ ...contactForm, address: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Notes..."
                value={contactForm.notes}
                onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveContact} data-testid="save-contact-btn">
              {editingContact ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Dialog */}
      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans']">Nouveau groupe</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom du groupe</Label>
              <Input
                placeholder="Ex: Famille, Travail..."
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Couleur</Label>
              <div className="flex gap-2">
                {['#0066DA', '#22C55E', '#EF4444', '#EAB308', '#8B5CF6', '#F97316'].map((color) => (
                  <button
                    key={color}
                    className={cn(
                      "w-8 h-8 rounded-full transition-transform",
                      newGroupColor === color && "ring-2 ring-offset-2 ring-primary scale-110"
                    )}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewGroupColor(color)}
                  />
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateGroup}>Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
